import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Check, X, MapPin, Calendar, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/public/quote/${token}`).then((r) => setQuote(r.data.quote)).catch(() => setQuote(null)), [token]);
  useEffect(() => { load(); }, [load]);

  const respond = async (action, tier) => {
    setBusy(true);
    try { const { data } = await api.post(`/public/quote/${token}/respond`, { action, tier }); setQuote((q) => ({ ...q, status: data.status, accepted_tier: data.accepted_tier })); toast.success(action === "accept" ? (tier ? `${tier} package accepted!` : "Quotation accepted!") : "Response recorded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (quote === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#0b0a1f]"><Sparkles className="h-8 w-8 animate-pulse text-fuchsia-400" /></div>;
  if (quote === null) return <div className="flex min-h-screen items-center justify-center bg-[#0b0a1f] text-white/70">This quotation link is invalid or has expired.</div>;

  const responded = ["accepted", "rejected"].includes(quote.status);
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0b0a1f]">
      <div className="aurora"><div className="blob" style={{ background: "#3b82f6", width: "40%", height: "50%", left: "-4%", top: "0%" }} /><div className="blob" style={{ background: "#7c3aed", width: "44%", height: "56%", right: "-6%", top: "-8%" }} /><div className="blob" style={{ background: "#db2777", width: "36%", height: "50%", left: "34%", bottom: "-14%" }} /></div>
      <div className="relative z-10 mx-auto max-w-2xl px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="print-area relative rounded-3xl bg-white/95 p-7 shadow-2xl backdrop-blur">
          <div className="mb-4 flex items-center justify-end no-print">
            <button onClick={() => window.print()} data-testid="quote-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300">Print / Save as PDF</button>
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Quotation</p>
              <h1 className="font-brand text-2xl font-bold text-slate-900">{quote.title}</h1>
            </div>
            <div className="flex shrink-0 items-center gap-2.5">
              {quote.agency?.logo && <img src={quote.agency.logo} alt={quote.agency?.name || "Agency"} data-testid="quote-agency-logo" className="h-11 w-11 rounded-xl border border-slate-100 object-cover" />}
              <div className="text-right"><p className="text-sm font-semibold text-slate-700">{quote.agency?.name}</p><p className="text-xs text-slate-500">{quote.agency?.phone}</p></div>
            </div>
          </div>

          {quote.poster_image && (
            <img src={quote.poster_image} alt="Trip poster" data-testid="quote-poster" className="mt-5 w-full rounded-2xl shadow-lg" />
          )}

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {quote.destination && <Row icon={MapPin} label="Destination" value={quote.destination} />}
            {quote.validity && <Row icon={Calendar} label="Valid Until" value={quote.validity} />}
          </div>

          {quote.customer_price != null && quote.customer_price !== "" && (
            <div className="mt-5 rounded-2xl bg-gradient-to-r from-blue-500 to-fuchsia-500 p-5 text-white">
              <p className="text-sm text-white/80">Total Price</p>
              <p className="font-brand text-3xl font-extrabold">₹{Number(quote.customer_price).toLocaleString("en-IN")}</p>
            </div>
          )}

          {quote.inclusions && (
            <div className="mt-5"><h3 className="font-semibold text-slate-800">Inclusions</h3><p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{quote.inclusions}</p></div>
          )}

          {quote.options?.length > 0 && (
            <div className="mt-5" data-testid="quote-options">
              <h3 className="font-semibold text-slate-800">Choose your package</h3>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                {quote.options.map((o, i) => (
                  <div key={i} className={`flex flex-col rounded-2xl border p-4 ${o.tier === "Best Value" ? "border-fuchsia-300 bg-fuchsia-50/50 ring-1 ring-fuchsia-200" : "border-slate-200 bg-white"} ${quote.accepted_tier === o.tier ? "ring-2 ring-emerald-400" : ""}`} data-testid={`quote-option-${i}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-600">{o.tier}</p>
                    {o.price != null && o.price !== "" && <p className="font-brand text-2xl font-extrabold text-slate-900">₹{Number(o.price).toLocaleString("en-IN")}</p>}
                    {o.summary && <p className="mt-1 text-xs text-slate-500">{o.summary}</p>}
                    {o.inclusions && <p className="mt-2 whitespace-pre-wrap text-[11px] text-slate-500">{o.inclusions}</p>}
                    {!responded ? (
                      <button onClick={() => respond("accept", o.tier)} disabled={busy} data-testid={`quote-accept-tier-${i}`}
                        className="mt-3 rounded-xl bg-emerald-500 py-2 text-xs font-semibold text-white disabled:opacity-50 hover:bg-emerald-600">Accept {o.tier}</button>
                    ) : quote.accepted_tier === o.tier ? (
                      <span className="mt-3 inline-flex items-center justify-center gap-1 rounded-xl bg-emerald-50 py-2 text-xs font-semibold text-emerald-600" data-testid={`quote-accepted-tier-${i}`}><CheckCircle2 className="h-3.5 w-3.5" /> Accepted</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {responded ? (
            <div className={`mt-6 flex items-center gap-2 rounded-xl p-3 text-sm font-medium ${quote.status === "accepted" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
              <CheckCircle2 className="h-4 w-4" /> You have {quote.status} this quotation{quote.status === "accepted" && quote.accepted_tier ? ` — ${quote.accepted_tier} package` : ""}.
            </div>
          ) : (
            <div className="mt-6 flex gap-3">
              {!(quote.options?.length > 0) && (
                <button onClick={() => respond("accept")} disabled={busy} data-testid="quote-accept" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 font-semibold text-white disabled:opacity-50"><Check className="h-4 w-4" /> Accept</button>
              )}
              {quote.options?.length > 0 && <p className="flex-1 self-center text-xs text-slate-400">Pick a package above to accept, or decline below.</p>}
              <button onClick={() => respond("reject")} disabled={busy} data-testid="quote-reject" className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 font-medium text-slate-600"><X className="h-4 w-4" /> Decline</button>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <Logo variant="sm" />
            <span className="flex items-center gap-1 text-[11px] text-slate-400"><ShieldCheck className="h-3 w-3" /> Powered by Suffle Online</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white/70 p-3">
      <Icon className="h-4 w-4 text-blue-500" />
      <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-700">{value}</p></div>
    </div>
  );
}
